import { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';

export default function useMenuCompletionState(defaultOptions = {}) {
  const defaultMealSlot = String(defaultOptions?.mealSlot || '').trim();
  const [isMenuCompleted, setIsMenuCompleted] = useState(false);
  const [consumedAt, setConsumedAt] = useState(null);
  const [mealCompletionState, setMealCompletionState] = useState(null);
  const [completionState, setCompletionState] = useState('active');
  const [resolvedMealSlot, setResolvedMealSlot] = useState(defaultMealSlot || null);
  const [loading, setLoading] = useState(true);

  const refreshMenuCompletionState = useCallback(async (options = {}) => {
    try {
      const token = localStorage.getItem('authToken');
      const mealSlot = String(options?.mealSlot || '').trim();
      const query = mealSlot ? `?mealSlot=${encodeURIComponent(mealSlot)}` : '';
      const res = await fetch(`${API_BASE_URL}/api/recipes/menu-completion/today${query}`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || 'menu_completion_fetch_failed');
      const completed = Boolean(json?.isMenuCompleted || json?.state === 'consumed');
      setIsMenuCompleted(completed);
      setConsumedAt(json?.consumedAt || null);
      setMealCompletionState(json?.mealCompletionState || null);
      setCompletionState(String(json?.state || 'active'));
      setResolvedMealSlot(
        json?.slotValidation?.resolvedMealSlot
          || json?.mealSlot
          || json?.mealCompletionState?.requestedMealSlot
          || mealSlot
          || null
      );
      return {
        completed,
        state: String(json?.state || 'active'),
        mealCompletionState: json?.mealCompletionState || null,
        resolvedMealSlot:
          json?.slotValidation?.resolvedMealSlot
          || json?.mealSlot
          || json?.mealCompletionState?.requestedMealSlot
          || mealSlot
          || null
      };
    } catch (_error) {
      setIsMenuCompleted(false);
      setConsumedAt(null);
      setMealCompletionState(null);
      setCompletionState('active');
      setResolvedMealSlot(String(options?.mealSlot || defaultMealSlot || '').trim() || null);
      return {
        completed: false,
        state: 'active',
        mealCompletionState: null,
        resolvedMealSlot: String(options?.mealSlot || defaultMealSlot || '').trim() || null
      };
    } finally {
      setLoading(false);
    }
  }, [defaultMealSlot]);

  useEffect(() => {
    refreshMenuCompletionState(defaultMealSlot ? { mealSlot: defaultMealSlot } : {});
  }, [refreshMenuCompletionState, defaultMealSlot]);

  const activeSlotState =
    resolvedMealSlot && mealCompletionState?.slots?.[resolvedMealSlot]
      ? mealCompletionState.slots[resolvedMealSlot]
      : null;
  const isCurrentMealCompleted = Boolean(activeSlotState?.status === 'completed');

  return {
    isMenuCompleted,
    isCurrentMealCompleted,
    consumedAt,
    mealCompletionState,
    completionState,
    resolvedMealSlot,
    activeSlotState,
    loading,
    refreshMenuCompletionState,
    setIsMenuCompleted
  };
}
